import { createColumnHelper } from "@tanstack/react-table";
import { FlightTable } from "../../../../../enum/flights/FlightTableEnum";
import { ensureArray } from "../../../../../utils/common/CommonHelperFunctions";
import { formatGroundHandler } from "../../../../../utils/flights/FlightHelperFunctions";
import {
  getAtmCategoryAbbreviation,
  getDelayDuration,
  getFlightSecurityLevelAbbreviation,
} from "../../../../../utils/flights/flight-table/FlightTableHelper";
import { DelayCode } from "../DelayCode";

const columnHelper = createColumnHelper<FlightTable>();

export const flightTableColumns = [
  columnHelper.accessor((row) => row.airline ?? "", {
    id: "common.airline",
    header: "AL",
  }),
  columnHelper.accessor((row) => row.routing ?? "", {
    id: "common.routing",
    header: "RTE",
  }),
  columnHelper.accessor((row) => row.arrival?.flightNumber ?? "", {
    id: "arrival.flight number",
    header: () => <div className="amd-container-vcenter">ARR FLT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.flightNumber ?? "", {
    id: "departure.flight number",
    header: () => <div className="amd-container-vcenter">DEP FLT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.displayStatus ?? "", {
    id: "arrival.status",
    header: () => <div className="amd-container-vcenter">ARR STAT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.displayStatus ?? "", {
    id: "departure.status",
    header: () => <div className="amd-container-vcenter">DEP STAT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.origin ?? "", {
    id: "arrival.origin",
    header: () => <div className="amd-container-vcenter">ORIG</div>,
  }),
  columnHelper.accessor((row) => row.departure?.destination ?? "", {
    id: "departure.destination",
    header: () => <div className="amd-container-vcenter">DEST</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.sibt ?? "", {
    id: "arrival.SIBT",
    header: () => <div className="amd-container-vcenter">SIBT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.eibt ?? "", {
    id: "arrival.EIBT",
    header: () => <div className="amd-container-vcenter">EIBT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.aibt ?? "", {
    id: "arrival.AIBT",
    header: () => <div className="amd-container-vcenter">AIBT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.flightDuration ?? "", {
    id: "arrival.flight duration",
    header: () => <div className="amd-container-vcenter">ARR FLT DUR</div>,
    cell: ({ getValue }) => {
      const v = getValue<number | null>();
      return v === 0 || v == null ? "" : String(v);
    },
  }),
  columnHelper.accessor(
    (row) =>
      getFlightSecurityLevelAbbreviation(row.arrival?.flightSecurityLevel),
    {
      id: "arrival.flight security level",
      header: () => <div className="amd-container-vcenter">ARR D/I</div>,
    }
  ),
  columnHelper.accessor((row) => row.arrival?.aircraftType ?? "", {
    id: "arrival.aircraft type",
    header: () => <div className="amd-container-vcenter">ARR AC TYPE</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.callSign ?? "", {
    id: "arrival.call sign",
    header: () => <div className="amd-container-vcenter">ARR CS</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.terminal ?? "", {
    id: "arrival.terminal",
    header: () => <div className="amd-container-vcenter">ARR TML</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.runway ?? "", {
    id: "arrival.runway",
    header: () => <div className="amd-container-vcenter">ARR RWY</div>,
  }),
  columnHelper.accessor((row) => getAtmCategoryAbbreviation(row.arrival?.atm), {
    id: "arrival.ATM",
    header: () => <div className="amd-container-vcenter">ARR ATM</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.pax.toString() ?? "", {
    id: "arrival.pax",
    header: () => <div className="amd-container-vcenter">ARR PAX</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.stand ?? "", {
    id: "arrival.stand",
    header: () => <div className="amd-container-vcenter">ARR STAND</div>,
  }),
  columnHelper.accessor(
    (row) => getDelayDuration(row.arrival?.delayDurations),
    {
      id: "arrival.delay duration.kpi",
      header: () => <div className="amd-container-vcenter">ARR DELAY</div>,
    }
  ),
  columnHelper.accessor((row) => ensureArray<string>(row.arrival?.delayCodes), {
    id: "arrival.delay code.kpi",
    header: () => <div className="amd-container-vcenter">ARR DLY C</div>,
    cell: (info) => {
      const arrCodes = info.getValue();
      const durations = ensureArray<string>(
        info.row.original.arrival?.delayDurations
      );
      return DelayCode({ codes: arrCodes, durations });
    },
    filterFn: (row, columnId, filterValue) => {
      const arrCodesList = row
        .getValue<string[]>(columnId)
        .join(",")
        .toLowerCase();
      const searchValue = String(filterValue ?? "").toLowerCase();
      return arrCodesList.includes(searchValue);
    },
  }),
  columnHelper.accessor((row) => row.arrival?.tibt ?? "", {
    id: "arrival.TIBT",
    header: () => <div className="amd-container-vcenter">TIBT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.pibt ?? "", {
    id: "arrival.PIBT",
    header: () => <div className="amd-container-vcenter">PIBT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.cibt ?? "", {
    id: "arrival.CIBT",
    header: () => <div className="amd-container-vcenter">ARR CIBT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.eldt ?? "", {
    id: "arrival.ELDT",
    header: () => <div className="amd-container-vcenter">ELDT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.aldt ?? "", {
    id: "arrival.ALDT",
    header: () => <div className="amd-container-vcenter">ALDT</div>,
  }),
  columnHelper.accessor(
    (row) => formatGroundHandler(row.arrival?.groundHandler),
    {
      id: "arrival.ground handler",
      header: () => <div className="amd-container-vcenter">ARR GHA</div>,
    }
  ),
  columnHelper.accessor((row) => row.arrival?.baggageBelt ?? "", {
    id: "arrival.baggage belt",
    header: () => <div className="amd-container-vcenter">ARR BELT</div>,
  }),
  columnHelper.accessor((row) => row.arrival?.gate ?? "", {
    id: "arrival.gate",
    header: () => <div className="amd-container-vcenter">ARR GATE</div>,
  }),
  columnHelper.accessor((row) => row.aircraftRegistrationNumber ?? "", {
    id: "common.aircraft registration number",
    header: "AC REG",
  }),
  columnHelper.accessor((row) => row?.expectedTat?.toString() ?? "", {
    id: "common.ETAT.kpi",
    header: "STT",
  }),
  columnHelper.accessor((row) => row?.actualTat?.toString() ?? "", {
    id: "common.ATAT.kpi",
    header: "ATT",
  }),
  columnHelper.accessor((row) => row?.turnAroundDelay?.toString() ?? "", {
    id: "common.turnaround Delay.kpi",
    header: "TAT DLY",
  }),
  columnHelper.accessor((row) => row.departure?.sobt ?? "", {
    id: "departure.SOBT",
    header: () => <div className="amd-container-vcenter">SOBT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.eobt ?? "", {
    id: "departure.EOBT",
    header: () => <div className="amd-container-vcenter">EOBT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.aobt ?? "", {
    id: "departure.AOBT",
    header: () => <div className="amd-container-vcenter">AOBT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.flightDuration ?? "", {
    id: "departure.flight duration",
    header: () => <div className="amd-container-vcenter">DEP FLT DUR</div>,
    cell: ({ getValue }) => {
      const v = getValue<number | null>();
      return v === 0 || v == null ? "" : String(v);
    },
  }),
  columnHelper.accessor(
    (row) =>
      getFlightSecurityLevelAbbreviation(row.departure?.flightSecurityLevel),
    {
      id: "departure.flight security level",
      header: () => <div className="amd-container-vcenter">DEP D/I</div>,
    }
  ),
  columnHelper.accessor((row) => row.departure?.aircraftType ?? "", {
    id: "departure.aircraft type",
    header: () => <div className="amd-container-vcenter">DEP AC TYPE</div>,
  }),
  columnHelper.accessor((row) => row.departure?.callSign ?? "", {
    id: "departure.call sign",
    header: () => <div className="amd-container-vcenter">DEP CS</div>,
  }),
  columnHelper.accessor((row) => row.departure?.terminal ?? "", {
    id: "departure.terminal",
    header: () => <div className="amd-container-vcenter">DEP TML</div>,
  }),
  columnHelper.accessor((row) => row.departure?.runway ?? "", {
    id: "departure.runway",
    header: () => <div className="amd-container-vcenter">DEP RWY</div>,
  }),
  columnHelper.accessor(
    (row) => getAtmCategoryAbbreviation(row.departure?.atm),
    {
      id: "departure.ATM",
      header: () => <div className="amd-container-vcenter">DEP ATM</div>,
    }
  ),
  columnHelper.accessor((row) => row.departure?.pax.toString() ?? "", {
    id: "departure.pax",
    header: () => <div className="amd-container-vcenter">DEP PAX</div>,
  }),
  columnHelper.accessor((row) => row.departure?.stand ?? "", {
    id: "departure.stand",
    header: () => <div className="amd-container-vcenter">DEP STAND</div>,
  }),
  columnHelper.accessor((row) => row.departure?.pobt ?? "", {
    id: "departure.POBT",
    header: () => <div className="amd-container-vcenter">POBT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.asat ?? "", {
    id: "departure.ASAT",
    header: () => <div className="amd-container-vcenter">ASAT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.atot ?? "", {
    id: "departure.ATOT",
    header: () => <div className="amd-container-vcenter">ATOT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.ctot ?? "", {
    id: "departure.CTOT",
    header: () => <div className="amd-container-vcenter">CTOT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.ttot ?? "", {
    id: "departure.TTOT",
    header: () => <div className="amd-container-vcenter">TTOT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.tsat ?? "", {
    id: "departure.TSAT",
    header: () => <div className="amd-container-vcenter">TSAT</div>,
  }),
  columnHelper.accessor(
    (row) => getDelayDuration(row.departure?.delayDurations) ?? "",
    {
      id: "departure.delay duration.kpi",
      header: () => <div className="amd-container-vcenter"> DEP DELAY</div>,
    }
  ),
  columnHelper.accessor(
    (row) => ensureArray<string>(row.departure?.delayCodes),
    {
      id: "departure.delay code.kpi",
      header: () => <div className="amd-container-vcenter">DEP DLY C</div>,
      cell: (info) => {
        const depCodes = info.getValue();
        const durations = ensureArray<string>(
          info.row.original.departure?.delayDurations
        );
        return DelayCode({ codes: depCodes, durations });
      },
      filterFn: (row, columnId, filterValue) => {
        const depCodesList = row
          .getValue<string[]>(columnId)
          .join(",")
          .toLowerCase();

        const searchValue = String(filterValue ?? "").toLowerCase();
        return depCodesList.includes(searchValue);
      },
    }
  ),
  columnHelper.accessor((row) => row.departure?.tobt ?? "", {
    id: "departure.TOBT",
    header: () => <div className="amd-container-vcenter">TOBT</div>,
  }),
  columnHelper.accessor((row) => row.departure?.runwayDelay ?? "", {
    id: "departure.runway delay",
    header: () => <div className="amd-container-vcenter">DEP RWY DLY</div>,
  }),
  columnHelper.accessor(
    (row) => formatGroundHandler(row.departure?.groundHandler),
    {
      id: "departure.ground handler",
      header: () => <div className="amd-container-vcenter">DEP GHA</div>,
    }
  ),
  columnHelper.accessor((row) => row.departure?.baggageChute ?? "", {
    id: "departure.baggage chute",
    header: () => <div className="amd-container-vcenter">DEP CHUTE</div>,
  }),
  columnHelper.accessor((row) => row.departure?.counter ?? "", {
    id: "departure.counter",
    header: () => <div className="amd-container-vcenter">DEP CTR</div>,
  }),
  columnHelper.accessor((row) => row.departure?.counterGroup ?? "", {
    id: "departure.counter group",
    header: () => <div className="amd-container-vcenter">DEP CTR GRP</div>,
  }),
  columnHelper.accessor((row) => row.departure?.counterType ?? "", {
    id: "departure.counter type",
    header: () => <div className="amd-container-vcenter">DEP CTR TYP</div>,
  }),
  columnHelper.accessor((row) => row.departure?.gate ?? "", {
    id: "departure.gate",
    header: () => <div className="amd-container-vcenter">DEP GATE</div>,
  }),
];
